/**
 * The recruiting game plan on home (APS-259 step 4).
 *
 * Five steps that tick themselves from real data (GET /game-plan). The card
 * only renders what the server says; every action is handed back to the
 * Dashboard, which owns the school grid, the modals and navigation.
 */
import React, { useEffect, useState } from 'react';
import { CheckCircle, ChevronDown, ChevronUp, Eye, PlayCircle } from 'react-feather';
import { Button, Card } from '../ui';
import analytics from '../../services/analytics';
import { useResponsive } from '../../hooks/useResponsive';
import type { GamePlan, GamePlanStep, GamePlanStepKey } from '../../services/api';
import {
  ALL_DONE_LINE,
  UPGRADE_LINE,
  UPGRADE_TITLE,
  buttonLabel,
  cardTitle,
  coachViewsLine,
  copyCtx,
  doneLine,
  openLine,
  stepTitle,
} from './gamePlanCopy';

export type GamePlanAction = GamePlanStepKey | 'upgrade';

interface Props {
  plan: GamePlan;
  athleteId: string;
  /** How many athletes this login has, for the viewed event. */
  athleteCount: number;
  /** The step to outline briefly, after the welcome video closes. */
  highlightKey?: GamePlanStepKey | null;
  onAction: (action: GamePlanAction, step?: GamePlanStep) => void;
  onWatchWelcome: () => void;
  /** Resolves true when the card was hidden. */
  onHide: () => Promise<boolean>;
}

export const GAME_PLAN_CARD_ID = 'game-plan-card';

const collapseKey = (athleteId: string) => `gamePlanCollapsed:${athleteId}`;

function readCollapsed(athleteId: string): boolean {
  try { return localStorage.getItem(collapseKey(athleteId)) === '1'; } catch { return false; }
}

function writeCollapsed(athleteId: string, collapsed: boolean) {
  try {
    if (collapsed) localStorage.setItem(collapseKey(athleteId), '1');
    else localStorage.removeItem(collapseKey(athleteId));
  } catch { /* storage off: the card just opens expanded next time */ }
}

// One viewed event per athlete per page load.
const viewedThisLoad = new Set<string>();

const GamePlanCard: React.FC<Props> = ({ plan, athleteId, athleteCount, highlightKey, onAction, onWatchWelcome, onHide }) => {
  const { isMobile } = useResponsive();
  const [collapsed, setCollapsed] = useState(() => readCollapsed(athleteId));
  const [hiding, setHiding] = useState(false);

  useEffect(() => { setCollapsed(readCollapsed(athleteId)); }, [athleteId]);

  useEffect(() => {
    if (viewedThisLoad.has(athleteId)) return;
    viewedThisLoad.add(athleteId);
    try {
      analytics.track('game_plan_viewed', {
        done_count: plan.done_count,
        open_steps: plan.steps.filter((s) => !s.done).map((s) => s.key),
        role: plan.viewer?.role || (plan.athlete.is_self ? 'athlete' : 'guardian'),
        athlete_count: athleteCount,
      });
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [athleteId]);

  // A highlight opens the card so the outline can be seen.
  useEffect(() => {
    if (highlightKey && collapsed) setCollapsed(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightKey]);

  const ctx = copyCtx(plan);
  const total = plan.total || plan.steps.length || 5;
  const allDone = plan.done_count >= total;
  const firstOpen = plan.steps.find((s) => !s.done)?.key;
  const pct = Math.max(0, Math.min(100, Math.round((plan.done_count / total) * 100)));

  const toggleCollapsed = () => {
    const next = !collapsed;
    setCollapsed(next);
    writeCollapsed(athleteId, next);
    try { analytics.track(next ? 'game_plan_collapsed' : 'game_plan_expanded', {}); } catch {}
  };

  const clickStep = (step: GamePlanStep, position: number) => {
    try { analytics.track('game_plan_step_clicked', { step: step.key, done: step.done, position }); } catch {}
    onAction(step.key, step);
  };

  const hide = async () => {
    setHiding(true);
    try {
      const hidden = await onHide();
      if (hidden) {
        try { analytics.track('game_plan_hidden', {}); } catch {}
      }
    } finally {
      setHiding(false);
    }
  };

  const progress = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
      <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--aps-text-2)', whiteSpace: 'nowrap' }}>
        {plan.done_count} of {total} done
      </span>
      <div
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuenow={plan.done_count}
        aria-label="Game plan progress"
        style={{
          flex: isMobile ? 1 : undefined,
          width: isMobile ? undefined : 120,
          height: 6,
          borderRadius: 999,
          background: 'var(--aps-fill)',
          overflow: 'hidden',
        }}
      >
        <div style={{ width: `${pct}%`, height: '100%', background: 'var(--aps-success)', borderRadius: 999, transition: 'width var(--aps-dur-count) var(--aps-ease-out)' }} />
      </div>
    </div>
  );

  const watchButton = (
    <button
      type="button"
      onClick={onWatchWelcome}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 0',
        background: 'transparent', border: 'none', cursor: 'pointer',
        fontFamily: 'var(--aps-font-sans)', fontSize: 13, fontWeight: 600,
        color: 'var(--aps-action-on-tint)', whiteSpace: 'nowrap',
      }}
    >
      <PlayCircle size={15} />
      Watch welcome video
    </button>
  );

  const chevron = (
    <button
      type="button"
      onClick={toggleCollapsed}
      aria-expanded={!collapsed}
      aria-controls="game-plan-steps"
      aria-label={collapsed ? 'Show game plan' : 'Hide game plan steps'}
      style={{
        width: 32, height: 32, flexShrink: 0, borderRadius: 999, border: 'none',
        background: 'var(--aps-fill)', color: 'var(--aps-text-2)', cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      {collapsed ? <ChevronDown size={18} /> : <ChevronUp size={18} />}
    </button>
  );

  return (
    <Card id={GAME_PLAN_CARD_ID} padding={isMobile ? 16 : 20} style={{ marginBottom: 16, scrollMarginTop: 80 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2 style={{ margin: 0, fontSize: 17, fontWeight: 800, color: 'var(--aps-ink)', lineHeight: 1.3 }}>
            {cardTitle(plan)}
          </h2>
          {isMobile && <div style={{ marginTop: 6 }}>{progress}</div>}
          {allDone && !collapsed && (
            <p style={{ margin: '6px 0 0', fontSize: 14, color: 'var(--aps-text-2)', lineHeight: 1.5 }}>{ALL_DONE_LINE}</p>
          )}
          {isMobile && !collapsed && <div style={{ marginTop: 4 }}>{watchButton}</div>}
        </div>
        {/* Desktop: the progress stays on the right, open or collapsed. */}
        {!isMobile && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexShrink: 0, paddingTop: 2 }}>
            {watchButton}
            {progress}
          </div>
        )}
        {chevron}
      </div>

      {!collapsed && (
        <div id="game-plan-steps">
          <ol style={{ listStyle: 'none', margin: '16px 0 0', padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {plan.steps.map((step, i) => {
              const isFirstOpen = step.key === firstOpen;
              const highlighted = highlightKey === step.key;
              return (
                <li
                  key={step.key}
                  style={{
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    alignItems: isMobile ? 'stretch' : 'center',
                    gap: isMobile ? 10 : 16,
                    padding: '12px 14px',
                    borderRadius: 12,
                    background: step.done ? 'transparent' : isFirstOpen ? 'var(--aps-action-tint)' : 'var(--aps-surface-2)',
                    border: `1px solid ${isFirstOpen && !step.done ? 'var(--aps-action-border)' : 'var(--aps-border)'}`,
                    outline: highlighted ? '2px solid var(--aps-action)' : '2px solid transparent',
                    outlineOffset: 2,
                    transition: 'outline-color var(--aps-dur-base) var(--aps-ease-out)',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flex: 1, minWidth: 0 }}>
                    {step.done ? (
                      <CheckCircle size={24} aria-label="Done" style={{ color: 'var(--aps-success)', flexShrink: 0 }} />
                    ) : (
                      <span
                        aria-hidden
                        style={{
                          width: 24, height: 24, flexShrink: 0, borderRadius: 999,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 12, fontWeight: 800,
                          background: isFirstOpen ? 'var(--aps-action)' : 'var(--aps-surface)',
                          color: isFirstOpen ? 'var(--aps-on-ink)' : 'var(--aps-text-2)',
                          border: isFirstOpen ? 'none' : '1.5px solid var(--aps-border-strong)',
                          boxSizing: 'border-box',
                        }}
                      >
                        {i + 1}
                      </span>
                    )}
                    <div style={{ minWidth: 0 }}>
                      <div style={{
                        fontSize: 15, fontWeight: 700, lineHeight: 1.35,
                        color: step.done ? 'var(--aps-text-muted)' : 'var(--aps-ink)',
                      }}>
                        {stepTitle(step.key, ctx)}
                      </div>
                      <div style={{ marginTop: 2, fontSize: 13, lineHeight: 1.45, color: 'var(--aps-text-muted)' }}>
                        {step.done ? doneLine(step, ctx) : openLine(step, plan, ctx)}
                      </div>
                    </div>
                  </div>
                  {!step.done && (
                    <Button
                      variant={isFirstOpen ? 'primary' : 'ghost'}
                      size="sm"
                      onClick={() => clickStep(step, i + 1)}
                      style={{
                        flexShrink: 0,
                        marginLeft: isMobile ? (isFirstOpen ? 36 : 28) : 0,
                        alignSelf: isMobile ? 'flex-start' : 'center',
                        whiteSpace: 'nowrap',
                        ...(isFirstOpen ? {} : { color: 'var(--aps-action-on-tint)' }),
                      }}
                    >
                      {buttonLabel(step)}
                    </Button>
                  )}
                </li>
              );
            })}
          </ol>

          {/* Coach views */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14, fontSize: 13, color: 'var(--aps-text-muted)' }}>
            <Eye size={15} style={{ color: plan.coach_views.count > 0 ? 'var(--aps-success)' : 'var(--aps-text-subtle)', flexShrink: 0 }} />
            <span>{coachViewsLine(plan)}</span>
          </div>

          {plan.upgrade.show && (
            <div
              style={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                alignItems: isMobile ? 'stretch' : 'center',
                gap: 12,
                marginTop: 14,
                padding: '12px 14px',
                borderRadius: 12,
                background: 'var(--aps-fill)',
                border: '1px solid var(--aps-border)',
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--aps-ink)' }}>{UPGRADE_TITLE}</div>
                <div style={{ marginTop: 2, fontSize: 13, color: 'var(--aps-text-2)' }}>{UPGRADE_LINE}</div>
              </div>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  try { analytics.track('game_plan_upgrade_clicked', { remaining: plan.free_programs.remaining }); } catch {}
                  onAction('upgrade');
                }}
                style={{ alignSelf: isMobile ? 'flex-start' : 'center', whiteSpace: 'nowrap' }}
              >
                See plans
              </Button>
            </div>
          )}

          {allDone && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
              <Button variant="ghost" size="sm" onClick={hide} loading={hiding}>
                Hide
              </Button>
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

export default GamePlanCard;
